import { GATSBY_DEV_ENV, IS_SSR, LocalStorageKeys, Settings, TestPanel } from '@/app'
import * as amplitude from '@amplitude/analytics-browser'
import * as sessionReplay from '@amplitude/session-replay-browser'
import { TypeGuards } from '@codeleap/types'
import { v4 } from 'uuid'
import { isAnalyticsEnabled } from './analytics'

class AmplitudeSessionReplayManager {
  private sessionId: number

  private ready = false

  get initialized() {
    return !this.sessionId ? false : TypeGuards.isNumber(this.sessionId)
  }

  async initialize() {
    if (!isAnalyticsEnabled()) return
    if (this.initialized) return

    const shouldInitialize = TestPanel.isEnabled() ? true : !GATSBY_DEV_ENV

    if (!shouldInitialize) return

    const sessionId = Date.now()

    const deviceId = this.getOrCreateDeviceId()

    try {
      await sessionReplay.init(Settings.ApiCredentials.Amplitude.API_KEY, {
        sessionId,
        deviceId,
        sampleRate: TestPanel.isProduction() ? 0.6 : 1,
        privacyConfig: {
          defaultMaskLevel: 'light'
        }
      }).promise

      await amplitude.init(Settings.ApiCredentials.Amplitude.API_KEY, {
        sessionId,
        deviceId
      }).promise

      this.sessionId = sessionId

      logger.log('[SessionReplay] Amplitude session replay initialized', {
        sessionId,
        deviceId,
        properties: this.getCurrentSessionReplayProperties()
      })
    } catch (err) {
      logger.error('[SessionReplay] Error on initialize amplitude session replay', err)
    } finally {
      this.ready = true
    }
  }

  deinitialize() {
    if (!this.initialized) return

    sessionReplay.shutdown()
    this.sessionId = undefined
  }

  getCurrentSessionReplayProperties() {
    if (!this.initialized) return {}

    return sessionReplay.getSessionReplayProperties()
  }

  private getOrCreateDeviceId(): string {
    if (IS_SSR) return undefined

    const existingDeviceId = localStorage.getItem(LocalStorageKeys.AMPLITUDE_DEVICE_ID)

    if (existingDeviceId) return existingDeviceId

    const newDeviceId = v4()

    localStorage.setItem(LocalStorageKeys.AMPLITUDE_DEVICE_ID, newDeviceId)

    return newDeviceId
  }

  async identify(userId: string | null | undefined) {
    if (!userId || !isAnalyticsEnabled()) return
    if (!this.ready) await this.initialize()

    amplitude.setUserId(userId)
    amplitude.identify(new amplitude.Identify())

    const replayProps = this.getCurrentSessionReplayProperties()
    amplitude.track('[web] session_link', replayProps)

    logger.log('[SessionReplay] User identified')
  }
}

export const sessionReplayManager = new AmplitudeSessionReplayManager()
